import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import JETOUR_IMAGES from "../../assets/images/jetourLogo.png";
import "./Login.css";
import { BASE_URL } from "../../api/Baseurl";
import { loginFailure, loginStart, loginSuccess } from "../../store/redux/slices/authSlice";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    userId: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    userId: "",
    password: "",
    apiError: "",
  });

  // Validation logic
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "userId":
        if (!value.trim()) {
          error = "User ID is required";
        } else if (!/^[a-zA-Z0-9-]{3,}$/.test(value)) {
          error = "Must be 3+ alphanumeric characters";
        }
        break;
      case "password":
        if (!value.trim()) {
          error = "Password is required";
        } else if (value.length < 4) {
          error = "Password must be at least 4 characters";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const validateForm = () => {
    const tempErrors = {
      userId: validateField("userId", formData.userId),
      password: validateField("password", formData.password),
      apiError: "",
    };
    setErrors(tempErrors);
    return Object.values(tempErrors).every((error) => !error);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submitted with data ********** : ", formData);

    setErrors((prev) => ({ ...prev, apiError: "" }));

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    dispatch(loginStart());

    // const controller = new AbortController();
    // const timeoutId = setTimeout(() => controller.abort(), 10000);

    console.log(`${BASE_URL}/Auth/Login?Username=${formData.userId}&Password=${formData.password}`);

    try {
      const response = await fetch(
        `${BASE_URL}/Auth/Login?Username=${formData.userId}&Password=${formData.password}`,
        {
          method: "GET",
          // signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Login response: ", response);

      if (!response.ok) {
        let errorMessage = "An unexpected error occurred";
        if (response.status === 401) {
          errorMessage = "Unauthorized. Check your credentials.";
        } else if (response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      console.log("Login data: ", data);

      if (data.Status === "1") {
        dispatch(
          loginSuccess({
            user: {
              userID: data?.Data?.UserID || '',
              userCode: data?.Data?.UserCode,
              userName: data?.Data?.UserName,
              branch: data?.Data?.Branch,
              branchName: data?.Data?.BranchName,
              Whs_01: data?.Data?.Whs_01,
              Whs_02: data?.Data?.Whs_02,
              Whs_03: data?.Data?.Whs_03,
              Whs_04: data?.Data?.Whs_04,
              Whs_05: data?.Data?.Whs_05,
              businessPartner: data?.Data?.BranchOEM || '',
              enabledBranches: data?.Data?.EnabledBranches || '',
              branchVendor: data?.Data?.BranchVendor || '',
              branchAddress: data?.Data?.BranchAddress || '',
              branchMobile: data?.Data?.BranchMobile || '',
            },
            token: data?.Data?.Token,
          })
        );
        setErrors({ userId: "", password: "", apiError: "" });
        navigate("/welcome");
      } else {
        console.log("Login failed with message: ", data);
        
        throw new Error(data.Message || "Login failed. Please try again.");
      }
    } catch (err) {
      let errorMessage = err.message || "An unexpected error occurred";
      if (err.name === "AbortError") {
        errorMessage = "Request timed out. Please check your connection.";
      }
      setErrors((prev) => ({ ...prev, apiError: errorMessage }));
      dispatch(loginFailure(errorMessage));
    } finally {
      // clearTimeout(timeoutId);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setErrors({ userId: "", password: "", apiError: "" });
    dispatch(loginFailure(""));
  }, [dispatch]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="jetour-login-container responsive-container">
      <div className="jetour-login-card responsive-login-card">
        <div className="jetour-login-brand-header responsive-brand-header">
          <img
            src={JETOUR_IMAGES}
            alt="Jetour Logo"
            className="jetour-login-logo responsive-logo"
            width="440"
            height="101"
          />
          <h2 className="jetour-login-brand-slogan responsive-title">Welcome to Jetour</h2>
          <p className="jetour-login-brand-subtitle">Sign in to manage your after-sales operations</p>
        </div>

        <form onSubmit={handleSubmit} className="jetour-login-form">
          <div className="jetour-login-form-group">
            <label htmlFor="userId">User ID</label>
            <div className="jetour-login-input-wrapper">
              <i className="bi bi-person jetour-login-input-icon"></i>
              <input
                type="text"
                id="userId"
                name="userId"
                value={formData.userId}
                onChange={handleChange}
                placeholder="Enter User ID"
                required
                className={errors.userId ? "jetour-login-input-error" : ""}
                disabled={isSubmitting}
              />
            </div>
            {errors.userId && (
              <span className="jetour-login-text-danger">{errors.userId}</span>
            )}
          </div>

          <div className="jetour-login-form-group">
            <label htmlFor="password">Password</label>
            <div className="jetour-login-input-wrapper">
              <i className="bi bi-lock jetour-login-input-icon"></i>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter Password"
                required
                className={errors.password ? "jetour-login-input-error" : ""}
                disabled={isSubmitting}
              />
              {/* <span
                className="jetour-login-password-toggle"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span> */}
            </div>
            {errors.password && (
              <span className="jetour-login-text-danger">{errors.password}</span>
            )}
          </div>

          <button
            type="submit"
            className={`jetour-login-button ${isSubmitting ? "jetour-login-disabled" : ""}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="jetour-login-loading-spinner">
                Signing In...
              </span>
            ) : (
              "Login"
            )}
          </button>

          {errors.apiError && (
            <span className="jetour-login-text-danger jetour-login-api-error">
              {errors.apiError}
            </span>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;